import type { RolloutWindow } from '@emma/api_client/types';
import { Box, IconButton, Text } from '@lego/klik-ui';
import { TrashSolid } from '@lego/klik-ui/icons';
import { UserNameLink } from '@/components/common/UserNameLink';
import { dateTimeFormat } from '@/utility/date';
import { sort } from '@/utility/sort';
import { PlaceholderText } from '../../PlaceholderText';
import { createColumnHelper } from '../appTable';
import { arrayIncludes, dateRange } from '../filters/filterFunctions';

const columnHelper = createColumnHelper<RolloutWindow>();

export const rolloutWindowsTableColumns = (
  canWrite: boolean,
  handleDeleteRolloutWindow: (rolloutWindowId: number) => void
) =>
  columnHelper.columns([
    columnHelper.accessor('id', {
      cell: (info) => (
        <Text fontFamily="mono" fontSize="sm">
          {info.getValue()}
        </Text>
      ),
      filterFn: 'includesString',
      header: 'ID',
      meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
      size: 96,
    }),
    columnHelper.accessor('durationInSeconds', {
      cell: (info) => {
        const value = info.getValue();
        return value ?? <PlaceholderText>N/A</PlaceholderText>;
      },
      header: 'Duration (seconds)',
      filterFn: 'includesString',
      meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
      sortFn: 'basic',
    }),
    columnHelper.accessor('cronExpression', {
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          <Text fontFamily="mono" fontSize="sm">
            {value}
          </Text>
        ) : (
          <PlaceholderText>N/A</PlaceholderText>
        );
      },
      header: 'Cron Expression',
      filterFn: 'includesString',
      meta: { filterVariant: { type: 'singleSearch' } },
    }),
    columnHelper.accessor('startTime', {
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          new Date(value).toLocaleString(dateTimeFormat.local)
        ) : (
          <PlaceholderText>N/A</PlaceholderText>
        );
      },
      filterFn: dateRange,
      header: 'Start Time',
      meta: { filterVariant: { type: 'dateRange' } },
      sortFn: ({ original: { startTime: a } }, { original: { startTime: b } }) => sort(a, b),
    }),
    columnHelper.accessor('endTime', {
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          new Date(value).toLocaleString(dateTimeFormat.local)
        ) : (
          <PlaceholderText>N/A</PlaceholderText>
        );
      },
      filterFn: dateRange,
      header: 'End Time',
      meta: { filterVariant: { type: 'dateRange' } },
      sortFn: ({ original: { endTime: a } }, { original: { endTime: b } }) => sort(a, b),
    }),
    columnHelper.accessor('createdByOid', {
      cell: (info) => <UserNameLink oid={info.getValue()} />,
      enableSorting: false,
      header: 'Created By',
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          new Date(value).toLocaleString(dateTimeFormat.local)
        ) : (
          <PlaceholderText>N/A</PlaceholderText>
        );
      },
      filterFn: dateRange,
      header: 'Created At',
      meta: { filterVariant: { type: 'dateRange' } },
      sortFn: ({ original: { createdAt: a } }, { original: { createdAt: b } }) => sort(a, b),
    }),
    columnHelper.accessor('status', {
      cell: (info) => info.getValue(),
      filterFn: arrayIncludes,
      header: 'Status',
      sortFn: 'basic',
    }),
    columnHelper.accessor('tag', {
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          <Text fontFamily="mono" fontSize="sm">
            {value}
          </Text>
        ) : (
          <PlaceholderText>N/A</PlaceholderText>
        );
      },
      filterFn: 'includesString',
      header: 'Tag',
      meta: { filterVariant: { type: 'singleSearch' } },
    }),
    ...(canWrite
      ? [
          columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
              return (
                <Box>
                  <IconButton
                    aria-label="Delete"
                    icon={<TrashSolid />}
                    inlineSize={'10'}
                    onClick={() => handleDeleteRolloutWindow(row.original.id)}
                    size={'sm'}
                  />
                </Box>
              );
            },
          }),
        ]
      : []),
  ]);
